import React, { useContext, useState, useEffect } from "react";
import { AuthContext } from "../App";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, LayoutDashboard, Target, BookOpen, MessageSquare, LogOut, 
  Bell, Menu, GraduationCap, FileText, Video, Users as UsersIcon, Award, Building2, User,
  Crown, Languages, Briefcase, Calendar, Trophy, Gift, TrendingUp, Mic, MessageCircle, MoreHorizontal, Bot, Dna, ArrowRightLeft
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import axios from "axios";
import { API } from "../App";
import Logo from "./Logo";

const Navbar = () => {
  const { user, logout, token } = useContext(AuthContext);
  const { currentLanguage, changeLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [languages, setLanguages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchLanguages();
    if (user && token) {
      fetchUnreadCount();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user, token]);

  const fetchLanguages = async () => {
    try {
      const response = await axios.get(`${API}/languages`);
      setLanguages(response.data.languages || []);
    } catch (error) {
      console.error("Failed to fetch languages", error);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch unread count", error);
    }
  };

  const handleLanguageChange = async (langCode) => {
    changeLanguage(langCode);
    
    if (user && token) {
      try {
        await axios.patch(`${API}/profile`, { language: langCode }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        console.error("Failed to update language preference", error);
      }
    }
  };

  const mainNavItems = [
    { icon: LayoutDashboard, labelKey: "nav.dashboard", path: "/dashboard", testId: "nav-dashboard" },
    { icon: Bot, labelKey: "nav.careerTwin", label: "My Career Twin", path: "/career-twin", testId: "nav-career-twin" },
    { icon: MessageSquare, labelKey: "nav.aiChat", path: "/chatbot", testId: "nav-chatbot" },
    { icon: Calendar, labelKey: "nav.studyPlanner", path: "/study-planner", testId: "nav-study-planner" },
  ];

  const assessItems = [
    { icon: Target, labelKey: "nav.assessment", path: "/assessment" },
    { icon: Target, labelKey: "nav.skillTests", path: "/skill-tests" },
    { icon: Mic, labelKey: "nav.interviewBuddy", path: "/interview-buddy" },
    { icon: Target, label: "Exam Prep", path: "/exam-prep", testId: "nav-exam-prep" },
    { icon: ArrowRightLeft, label: "Career Pivot Planner", path: "/career-pivot" },
    { icon: FileText, labelKey: "nav.resources", path: "/resources" },
  ];

  const exploreItems = [
    { icon: GraduationCap, labelKey: "nav.careers", path: "/careers" },
    { icon: BookOpen, labelKey: "nav.courses", path: "/courses" },
    { icon: BookOpen, label: "My Courses", path: "/my-courses" },
    { icon: Target, labelKey: "nav.careerComparison", path: "/career-comparison" },
    { icon: BookOpen, labelKey: "nav.scholarships", path: "/scholarships" },
    { icon: Award, labelKey: "nav.exams", path: "/exams" },
    { icon: Building2, labelKey: "nav.colleges", path: "/colleges" },
    { icon: Dna, labelKey: "nav.skillDNA", label: "Your Skill DNA", path: "/skill-dna" },
    { icon: Award, labelKey: "nav.quizzes", path: "/quizzes" },
    { icon: Briefcase, labelKey: "nav.opportunities", path: "/opportunities" },
  ];

  const engageItems = [
    { icon: Video, labelKey: "nav.events", path: "/events" },
    { icon: UsersIcon, labelKey: "nav.mentors", path: "/mentors" },
    { icon: Trophy, labelKey: "nav.badges", path: "/badges" },
    { icon: Gift, labelKey: "nav.rewards", path: "/rewards" },
    { icon: Video, labelKey: "nav.consultation", path: "/consultation/book" },
  ];

  const communityItems = [
    { icon: MessageSquare, labelKey: "nav.forum", path: "/community/forum" },
    { icon: UsersIcon, labelKey: "nav.mentoring", path: "/community/mentoring" },
    { icon: Award, labelKey: "nav.successStories", path: "/community/success-stories" },
    { icon: BookOpen, labelKey: "nav.studyGroups", path: "/community/study-groups" },
    { icon: MessageCircle, labelKey: "nav.qa", path: "/community/qa" },
  ];

  const upgradeItems = [
    { icon: Crown, labelKey: "nav.subscriptions", path: "/subscriptions" },
    { icon: Video, labelKey: "nav.myConsultations", path: "/consultations" },
  ];

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-border" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-2">
          {/* Logo */}
          <div 
            className="flex items-center cursor-pointer flex-shrink-0 min-w-0" 
            onClick={() => navigate(user ? "/dashboard" : "/")} 
            data-testid="nav-logo"
          >
            <Logo className="h-12 w-auto" linkTo={user ? "/dashboard" : "/"} />
          </div>

          {user ? (
          <>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1 flex-1 justify-center px-4">
            {/* Dashboard button */}
            <Button
              variant={location.pathname === "/dashboard" ? "default" : "ghost"}
              size="sm"
              className="gap-2 h-9"
              onClick={() => navigate("/dashboard")}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span className="font-medium">{t('nav.dashboard')}</span>
            </Button>

            {/* Assess Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 h-9">
                  <Target className="w-4 h-4" />
                  <span className="font-medium">{t('nav.assess')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('nav.assess')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {assessItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* AI Chat button */}
            <Button
              variant={location.pathname === "/chatbot" ? "default" : "ghost"}
              size="sm"
              className="gap-2 h-9"
              onClick={() => navigate("/chatbot")}
              data-testid="nav-chatbot"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="font-medium">{t('nav.aiChat')}</span>
            </Button>

            {/* Study Planner button */}
            <Button
              variant={location.pathname === "/study-planner" ? "default" : "ghost"}
              size="sm"
              className="gap-2 h-9"
              onClick={() => navigate("/study-planner")}
              data-testid="nav-study-planner"
            >
              <Calendar className="w-4 h-4" />
              <span className="font-medium">{t('nav.studyPlanner')}</span>
            </Button>

            {/* Explore Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 h-9">
                  <BookOpen className="w-4 h-4" />
                  <span className="font-medium">{t('nav.explore')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('nav.explore')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {exploreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Engage Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 h-9">
                  <UsersIcon className="w-4 h-4" />
                  <span className="font-medium">{t('nav.engage')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('nav.engage')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {engageItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Dropdown - Community & Support */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant={location.pathname.startsWith("/community") || location.pathname === "/support" ? "default" : "ghost"} 
                  size="sm" 
                  className="gap-2 h-9"
                >
                  <MoreHorizontal className="w-4 h-4" />
                  <span className="font-medium">More</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                {/* Community Section */}
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Community
                </DropdownMenuLabel>
                {communityItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
                
                <DropdownMenuSeparator />
                
                {/* Help & Support Section */}
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  Help & Support
                </DropdownMenuLabel>
                <DropdownMenuItem 
                  onClick={() => navigate("/support")}
                  className="cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4 mr-3 text-muted-foreground" />
                  <span className="font-medium">Support</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Upgrade Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 h-9">
                  <Crown className="w-4 h-4 text-warning" />
                  <span className="font-medium">{t('nav.upgrade')}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('nav.premiumFeatures')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {upgradeItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className="cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 h-9 px-2 sm:px-3">
                  <Languages className="w-4 h-4" />
                  <span className="hidden sm:inline text-xs uppercase font-semibold tracking-wide">{currentLanguage}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  {t('nav.selectLanguage')}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {languages.map((lang) => (
                  <DropdownMenuItem 
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={currentLanguage === lang.code ? "bg-accent" : "cursor-pointer"}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="font-medium">{lang.native_name}</span>
                      {currentLanguage === lang.code && (
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">
                          {t('common.active')}
                        </Badge>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Notifications */}
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/notifications")}
              className="relative h-9 w-9 p-0 hover:bg-accent flex-shrink-0"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px] bg-destructive hover:bg-destructive">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1 h-9 px-1 sm:px-2 flex-shrink-0">
                  <div className="w-7 h-7 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center">
                    {user?.profile_image ? (
                      <img 
                        src={user.profile_image} 
                        alt={user.name} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-4 h-4 text-primary" />
                    )}
                  </div>
                  <span className="hidden lg:inline max-w-[100px] truncate font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center flex-shrink-0">
                      {user?.profile_image ? (
                        <img 
                          src={user.profile_image} 
                          alt={user.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <User className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex flex-col space-y-1 flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
                  <User className="w-4 h-4 mr-3 text-muted-foreground" />
                  <span className="font-medium">{t('nav.profile')}</span>
                </DropdownMenuItem>
                {(user?.role === "admin" || user?.role === "school_admin") && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer">
                    <Building2 className="w-4 h-4 mr-3 text-muted-foreground" />
                    <span className="font-medium">{t('nav.admin')}</span>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="text-destructive cursor-pointer">
                  <LogOut className="w-4 h-4 mr-3" />
                  <span className="font-medium">{t('common.logout')}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" className="h-9 w-9 p-0 flex-shrink-0" data-testid="mobile-menu-btn">
                  <Menu className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 max-h-[calc(100vh-100px)] overflow-y-auto">
                <DropdownMenuLabel className="text-base font-semibold">{t('nav.navigation')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)} 
                      className="py-3 cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('nav.assess')}
                </DropdownMenuLabel>
                {assessItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)} 
                      className="py-3 cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('nav.explore')}
                </DropdownMenuLabel>
                {exploreItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)} 
                      className="py-3 cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">
                  {t('nav.engage')}
                </DropdownMenuLabel>
                {engageItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)} 
                      className="py-3 cursor-pointer"
                    >
                      <Icon className="w-4 h-4 mr-3 text-muted-foreground" />
                      <span className="font-medium">{item.label || t(item.labelKey)}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          </>
          ) : (
            /* Public Navigation - Not Logged In */
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
              >
                Home
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
              >
                Login
              </Button>
              <Button
                size="sm"
                onClick={() => navigate('/register')}
              >
                Get Started
              </Button>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
